import React, { useEffect, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, PrinterIcon } from 'lucide-react';
import { useIsMobile } from '../hooks/useIsMobile';
import { WeekData, StatRow } from '../data/mockData';
interface FullBriefingModalProps {
  isOpen: boolean;
  onClose: () => void;
  briefingCopy: WeekData['briefingCopy'];
}
export function FullBriefingModal({
  isOpen,
  onClose,
  briefingCopy: c
}: FullBriefingModalProps) {
  const isMobile = useIsMobile();
  useEffect(() => {
    if (isOpen) {
      const handleKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose();
      };
      window.addEventListener('keydown', handleKey);
      return () => window.removeEventListener('keydown', handleKey);
    }
  }, [isOpen, onClose]);
  return <AnimatePresence data-id="element-1">
      {isOpen && <>
          <motion.div key="modal-backdrop" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={onClose} style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.72)',
        backdropFilter: 'blur(4px)',
        zIndex: 60
      }} data-id="element-2" />

          <div style={{
        position: 'fixed',
        inset: 0,
        zIndex: 70,
        display: 'flex',
        alignItems: isMobile ? 'stretch' : 'flex-start',
        justifyContent: 'center',
        padding: isMobile ? '0' : '24px 24px',
        overflowY: isMobile ? 'hidden' : 'auto'
      }} data-id="element-3">
            <motion.div key="modal-content" initial={{
          opacity: 0,
          scale: isMobile ? 1 : 0.97,
          y: isMobile ? 40 : 8
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: isMobile ? 1 : 0.97,
          y: isMobile ? 40 : 8
        }} transition={{
          duration: 0.22,
          ease: [0.16, 1, 0.3, 1]
        }} onClick={e => e.stopPropagation()} style={{
          background: '#111827',
          border: isMobile ? 'none' : '1px solid rgba(255,255,255,0.07)',
          borderRadius: isMobile ? '0' : '16px',
          width: '100%',
          maxWidth: isMobile ? '100%' : '760px',
          display: 'flex',
          flexDirection: 'column',
          height: isMobile ? '100%' : 'auto',
          alignSelf: isMobile ? 'stretch' : 'flex-start'
        }} data-id="element-4">
              {/* Header */}
              <div style={{
            padding: isMobile ? '16px 20px' : '20px 28px',
            borderBottom: '1px solid rgba(255,255,255,0.07)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0
          }} data-id="element-5">
                <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px'
            }} data-id="element-6">
                  <span style={{
                fontSize: '11px',
                fontWeight: 600,
                color: '#4e5f74',
                letterSpacing: '0.12em',
                textTransform: 'uppercase'
              }} data-id="element-7">
                    {c.headerLabel}
                  </span>
                  {!isMobile && <span style={{
                fontSize: '12px',
                color: '#4e5f74'
              }} data-id="element-8">
                      {c.dateRange}
                    </span>}
                </div>
                <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }} data-id="element-9">
                  {!isMobile && <button onClick={() => window.print()} style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '6px',
                padding: '6px 10px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                color: '#8b9ab0',
                fontSize: '12px'
              }} data-id="element-10">
                      <PrinterIcon size={13} data-id="element-11" />
                      {c.printLabel}
                    </button>}
                  <button onClick={onClose} style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '6px',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                color: '#4e5f74'
              }} aria-label="Close briefing" data-id="element-12">
                    <XIcon size={18} data-id="element-13" />
                  </button>
                </div>
              </div>

              {/* Scrollable content */}
              <div style={{
            padding: isMobile ? '18px' : '24px 32px',
            overflowY: 'auto',
            flex: isMobile ? 1 : 'none',
            maxHeight: isMobile ? 'none' : 'calc(90vh - 80px)'
          }} data-id="element-14">
                {/* 1. Why This Matters */}
                <div style={{
              background: 'rgba(59,130,246,0.06)',
              border: '1px solid rgba(59,130,246,0.12)',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '28px'
            }} data-id="element-15">
                  <div style={{
                fontSize: '10px',
                fontWeight: 700,
                color: '#3b82f6',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                marginBottom: '6px'
              }} data-id="element-16">
                    {c.whyItMatters.title}
                  </div>
                  <p style={bodyStyle} data-id="element-17">{c.whyItMatters.body}</p>
                </div>

                {/* 2. Recommended Actions — command block treatment */}
                <div style={{
              marginBottom: '28px'
            }} data-id="element-18">
                  <h2 style={sectionHeadingStyle} data-id="element-19">
                    {c.sections.recommendedActions.title}
                  </h2>
                  <div style={{
                borderLeft: '2px solid rgba(59,130,246,0.35)',
                paddingLeft: '14px'
              }} data-id="element-20">
                    <ul style={{
                  margin: 0,
                  padding: 0,
                  listStyle: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }} data-id="element-21">
                      {c.sections.recommendedActions.items.map((text, i) => <CheckboxItem key={i} text={text} data-id="element-22" />)}
                    </ul>
                  </div>
                </div>

                {/* 3. Weekly/Monthly Overview — body + stat rows */}
                <div style={{
              marginBottom: '28px'
            }} data-id="element-23">
                  <h2 style={sectionHeadingStyle} data-id="element-24">
                    {c.sections.weeklyOverview.title}
                  </h2>
                  <p style={{
                ...bodyStyle,
                marginBottom: '14px'
              }} data-id="element-25">
                    {c.sections.weeklyOverview.body}
                  </p>
                  <div style={{
                borderTop: '1px solid rgba(255,255,255,0.05)'
              }} data-id="element-26">
                    {c.sections.weeklyOverview.statRows.map((row, i) => <StatRowItem key={i} row={row} data-id="element-27" />)}
                  </div>
                </div>

                {/* 4. Notable Patterns */}
                <BriefingSection title={c.sections.notablePatterns.title} data-id="element-28">
                  {c.sections.notablePatterns.subSections.map(s => <SubSection key={s.title} title={s.title} data-id="element-29">
                      <p style={bodyStyle} data-id="element-30">{s.body}</p>
                    </SubSection>)}
                </BriefingSection>

                {/* 5. Priority Entities */}
                <BriefingSection title={c.sections.priorityEntities.title} data-id="element-31">
                  <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }} data-id="element-32">
                    {c.sections.priorityEntities.items.map(item => <RepeatItem key={item.name} name={item.name} context={item.context} data-id="element-33" />)}
                  </div>
                </BriefingSection>

                {/* 6. Source Notes */}
                <BriefingSection title={c.sections.sourceNotes.title} isLast data-id="element-34">
                  <p style={{
                fontSize: '12px',
                color: '#4e5f74',
                lineHeight: '1.6',
                margin: 0
              }} data-id="element-35">
                    {c.sections.sourceNotes.body}
                  </p>
                </BriefingSection>
              </div>
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}
// ─── Styles ───────────────────────────────────────────────────────────────────
const sectionHeadingStyle: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 700,
  color: '#c8d3e0',
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  marginBottom: '10px',
  marginTop: 0,
  paddingBottom: '8px',
  borderBottom: '1px solid rgba(255,255,255,0.06)'
};
const bodyStyle: React.CSSProperties = {
  fontSize: '13px',
  color: '#8b9ab0',
  lineHeight: '1.65',
  margin: 0
};
const bulletListStyle: React.CSSProperties = {
  margin: 0,
  padding: '0 0 0 4px',
  listStyle: 'none',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px'
};
const bulletItemStyle: React.CSSProperties = {
  fontSize: '13px',
  color: '#8b9ab0',
  lineHeight: '1.6'
};
// ─── Section Components ───────────────────────────────────────────────────────
function BriefingSection({
  title,
  children,
  isLast
}: {
  title: string;
  children: React.ReactNode;
  isLast?: boolean;
}) {
  return <div style={{
    marginBottom: isLast ? 0 : '28px'
  }} data-id="element-36">
      <h2 style={sectionHeadingStyle} data-id="element-37">{title}</h2>
      {children}
    </div>;
}
function SubSection({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <div style={{
    marginBottom: '14px'
  }} data-id="element-38">
      <h3 style={{
      fontSize: '11px',
      fontWeight: 600,
      color: '#6b7a8d',
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      marginBottom: '6px',
      marginTop: 0
    }} data-id="element-39">
        {title}
      </h3>
      {children}
    </div>;
}
function RepeatItem({
  name,
  context
}: {
  name: string;
  context: string;
}) {
  return <div style={{
    display: 'flex',
    gap: '12px',
    padding: '9px 12px',
    background: 'rgba(255,255,255,0.03)',
    borderRadius: '6px',
    border: '1px solid rgba(255,255,255,0.07)'
  }} data-id="element-40">
      <span style={{
      fontSize: '13px',
      fontWeight: 700,
      color: '#c8d3e0',
      minWidth: '130px',
      flexShrink: 0
    }} data-id="element-41">
        {name}
      </span>
      <span style={{
      fontSize: '12px',
      color: '#8b9ab0',
      lineHeight: '1.5'
    }} data-id="element-42">
        {context}
      </span>
    </div>;
}
function CheckboxItem({
  text
}: {
  text: string;
}) {
  return <li style={{
    display: 'flex',
    gap: '10px',
    alignItems: 'flex-start'
  }} data-id="element-43">
      <span style={{
      fontSize: '13px',
      color: '#4e5f74',
      lineHeight: '1.5',
      flexShrink: 0,
      marginTop: '1px',
      fontFamily: 'monospace'
    }} data-id="element-44">
        □
      </span>
      <span style={{
      fontSize: '13px',
      fontWeight: 500,
      color: '#a0aec0',
      lineHeight: '1.55'
    }} data-id="element-45">
        {text}
      </span>
    </li>;
}
function StatRowItem({
  row
}: {
  row: StatRow;
}) {
  const deltaPositive = row.delta > 0;
  const deltaStr = deltaPositive ? `+${row.delta}` : `${row.delta}`;
  return <div style={{
    display: 'flex',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
    gap: '0'
  }} data-id="element-46">
      <span style={{
      fontSize: '13px',
      color: '#c8d3e0',
      fontWeight: 500,
      flex: 1
    }} data-id="element-47">
        {row.label}
      </span>
      <span style={{
      fontSize: '11px',
      color: '#4e5f74',
      fontVariantNumeric: 'tabular-nums',
      whiteSpace: 'nowrap',
      width: '64px',
      textAlign: 'right'
    }} data-id="element-48">
        {row.total}
      </span>
      <span style={{
      fontSize: '11px',
      fontWeight: 700,
      color: deltaPositive ? '#f87171' : '#4ade80',
      fontVariantNumeric: 'tabular-nums',
      whiteSpace: 'nowrap',
      width: '32px',
      textAlign: 'right'
    }} data-id="element-49">
        {deltaStr}
      </span>
    </div>;
}